import { Outlet, createRootRouteWithContext } from "@tanstack/react-router";
import { TanStackRouterDevtoolsPanel } from "@tanstack/react-router-devtools";
import { TanStackDevtools } from "@tanstack/react-devtools";
import { Suspense, lazy, useEffect, useState } from "react";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { extractUTMParams, storeUTMParams } from "@/lib/utmTracking";
import { resolveEntryFunnelFlow } from "@/lib/funnelFlow";
import useLegacyStorage from "@/hooks/useLegacyStorage";
import NotFound from "@/components/NotFound";
import GlobalError from "@/components/GlobalError";
import { useAuthStore } from "@/stores/useAuth";
import useUserRegister from "@/hooks/useUserRegister";
import ExclusiveBadge from "@/components/ExclusiveBadge";
import { TrackingProvider } from "@/components/ClientAnalytics";
import LoginModal from "@/modules/LoginModal";
import useAuth from "@/hooks/useAuth";
import UserInfosWatcher from "@/components/UserInfosWatcher";
import ZendeskWidget from "@/components/ZendeskWidget";
import DevToolsPanel from "@/components/DevToolsPanel";
import { useAmplitudeExperiment } from "@/hooks/useAmplitudeExperiment";
import { useAppStateStore, useUpsellOpen } from "@/stores/useAppState";

interface MyRouterContext {}

const UpsellFlow = lazy(() => import("@/modules/UpsellFlow"));

/** Post-purchase upsell, mounted globally so it shows over any route. */
const GlobalUpsell = () => {
  const upsellOpen = useUpsellOpen();
  const closeUpsell = useAppStateStore((s) => s.closeUpsell);

  if (!upsellOpen) return null;

  return (
    <Suspense fallback={null}>
      <UpsellFlow onClose={closeUpsell} />
    </Suspense>
  );
};

export const Route = createRootRouteWithContext<MyRouterContext>()({
  component: () => {
    const { transferStorage } = useLegacyStorage();
    const { getFingerprint } = useUserRegister();
    const { registerUser } = useAuth();
    const { bootABTestVariants } = useAmplitudeExperiment();
    const [loading, setLoading] = useState(true);

    const registerUserAndSetAccessToken = async () => {
      const { currentUser, deviceId: storedDeviceId } = useAuthStore.getState();
      const fingerprint = storedDeviceId || (await getFingerprint());

      await registerUser(currentUser?.id || fingerprint);

      if (!storedDeviceId) useAuthStore.setState({ deviceId: fingerprint });
    };

    const prepareApp = async () => {
      transferStorage();

      const { currentUser, accessToken } = useAuthStore.getState();

      if (!currentUser?.id || !accessToken) {
        await registerUserAndSetAccessToken();
      }

      // After the user, which the fetch is keyed on; before the first render,
      // which a first visit must not make on the control (SHO-852).
      await bootABTestVariants();

      setLoading(false);
    };

    useEffect(() => {
      prepareApp();
    }, []);

    return (
      <>
        <TrackingProvider>
          {loading ? (
            <div className="absolute top-0 left-0 flex h-full w-full items-center justify-center">
              <ExclusiveBadge className="size-24 animate-pulse text-neutral-50" />
            </div>
          ) : (
            <GoogleOAuthProvider
              clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID}
            >
              <LoginModal />
              <UserInfosWatcher />
              <ZendeskWidget />
              <GlobalUpsell />
              <Outlet />
            </GoogleOAuthProvider>
          )}
        </TrackingProvider>
        <TanStackDevtools
          config={{
            hideUntilHover: true,
            // Away from the bottom-right, which the Zendesk launcher and every
            // bottom bar's trailing control already share — the trigger is
            // `position: fixed; z-index: 99999` and silently swallowed their
            // clicks (My List's selection bar deletes from there).
            position: "bottom-left",
          }}
          plugins={[
            {
              name: "Tanstack Router",
              render: <TanStackRouterDevtoolsPanel />,
            },
            ...(import.meta.env.DEV
              ? [{ name: "App Dev Tools", render: <DevToolsPanel /> }]
              : []),
          ]}
        />
      </>
    );
  },
  beforeLoad: ({ search }) => {
    const utmParams = extractUTMParams(search);
    storeUTMParams(utmParams);
    resolveEntryFunnelFlow(search);
  },
  notFoundComponent: NotFound,
  errorComponent: (props) => <GlobalError {...props} feature="router" />,
});
